import { Answer } from '@/contexts/MasterclassContext';

import { Slide, Asset, MasterClassConfig, CaseConfig, SlideType } from '../types';
import {
  AnamneseSlide,
  SimpleSlide,
  QuestionSlide,
  QuestionMultipleSlide,
  VideoSlide,
} from './slides';
import { isMasterClassAnswerCorrect } from '../utils/answer-validator';

interface SlideFactoryProps {
  slide: Slide;
  assets: Asset[];
  assetsBaseUrl: string;
  masterClass: MasterClassConfig;
  onNext: () => void;
  onPrevious: () => void;
  onAnswerSelected: (answerId: string, isCorrect: boolean, videoRef?: string) => Promise<void>;
  answeredQuestions: Map<string, string[]>;
  allAnswers: Array<Answer>;
  caseConfig: CaseConfig;
}

export const SlideFactory = ({
  slide,
  assets,
  assetsBaseUrl,
  masterClass,
  onNext,
  onPrevious,
  onAnswerSelected,
  allAnswers,
  caseConfig,
}: SlideFactoryProps) => {
  const baseProps = { slide, assets, assetsBaseUrl, onNext, onPrevious };

  // Compute incorrect attempts for this slide from allAnswers
  // An answer is incorrect if it's not in the correct answers list
  const slideAnswers = allAnswers.filter((a) => a.slideId === slide.id);
  const correctAnswerIds = masterClass.questionMap?.[slide.id]?.correctAnswerIds || [];
  const correctAttempts: Map<string, string[]> = slideAnswers.reduce((acc, curr) => {
    // use isMasterClassAnswerCorrect to check if the answer is correct
    if (isMasterClassAnswerCorrect(caseConfig, slide.id, [curr.answerId])) {
      if (!acc.has(slide.id)) {
        acc.set(slide.id, []);
      }
      const existing = acc.get(slide.id)!;
      if (!existing.includes(curr.answerId)) {
        existing.push(curr.answerId);
        acc.set(slide.id, existing);
      }
    }
    return acc;
  }, new Map<string, string[]>());
  const incorrectAttempts: Record<string, string[]> = slideAnswers.reduce(
    (acc, a) => {
      if (!correctAnswerIds.includes(a.answerId)) {
        if (!acc[slide.id]) {
          acc[slide.id] = [];
        }
        if (!acc[slide.id].includes(a.answerId)) {
          acc[slide.id].push(a.answerId);
        }
      }
      return acc;
    },
    {} as Record<string, string[]>
  );

  switch (slide.type) {
    case SlideType.ANAMNESE:
      return <AnamneseSlide {...baseProps} slide={slide as any} />;

    case SlideType.NORMAL:
    case SlideType.IMAGE:
      return <SimpleSlide {...baseProps} slide={slide as any} />;

    case SlideType.VIDEO:
      return <VideoSlide {...baseProps} slide={slide as any} />;

    case SlideType.QUESTION_SINGLE: {
      if (!masterClass?.questionMap?.[slide.id]) {
        return (
          <div className="p-8 text-center">
            <p className="text-lg text-muted-foreground">
              Configuration de question manquante pour ce slide (ID: {slide.id})
            </p>
          </div>
        );
      }

      const questionCorrectAnswerIds = masterClass.questionMap[slide.id].correctAnswerIds;
      return (
        <QuestionSlide
          key={slide.id}
          {...baseProps}
          correctAnswerIds={questionCorrectAnswerIds}
          answerMap={masterClass.answerMap}
          onAnswerSelected={onAnswerSelected}
          correctAttempts={correctAttempts}
          incorrectAttempts={incorrectAttempts}
          caseConfig={caseConfig}
        />
      );
    }
    case SlideType.QUESTION_MULTIPLE:
    case SlideType.QUESTION_MULTIPLE_ORDERED: {
      if (!masterClass?.questionMap?.[slide.id]) {
        return (
          <div className="p-8 text-center">
            <p className="text-lg text-muted-foreground">
              Configuration de question manquante pour ce slide (ID: {slide.id})
            </p>
          </div>
        );
      }

      const multipleCorrectAnswerIds = masterClass.questionMap[slide.id].correctAnswerIds;
      return (
        <QuestionMultipleSlide
          {...baseProps}
          correctAnswerIds={multipleCorrectAnswerIds}
          answerMap={masterClass.answerMap}
          onAnswerSelected={onAnswerSelected}
          correctAttempts={correctAttempts}
          incorrectAttempts={incorrectAttempts}
          caseConfig={caseConfig}
        />
      );
    }
    case SlideType.SLIDE_SHOW:
      return (
        <div className="p-8 text-center">
          <p className="text-lg text-muted-foreground">
            Type de slide "Slide Show" pas encore implémenté
          </p>
        </div>
      );

    case SlideType.CUSTOM:
      return (
        <div className="p-8 text-center">
          <p className="text-lg text-muted-foreground">
            Type de slide "Custom" pas encore implémenté
          </p>
        </div>
      );

    default:
      return (
        <div className="text-center text-muted-foreground">
          Type de slide non supporté: {(slide as any).type}
        </div>
      );
  }
};
